<template>
  <div
    ref="r"
    class="tooltip-host"
    :class="{ 'tooltip-host--inline': inline }"
    v-bind="$attrs"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <slot></slot>
  </div>
  <Teleport to="body">
    <Transition
      name="tooltip"
      @after-enter="syncPopper"
    >
      <div
        v-if="show && visible"
        ref="rt"
        class="tooltip bs-tooltip-auto show"
        role="tooltip"
      >
        <div
          class="tooltip-arrow"
          data-popper-arrow
        ></div>
        <div class="tooltip-inner">{{ text }}</div>
      </div>
    </Transition>
  </Teleport>
</template>
<script>
let nextTooltipId = 0
/** @type {Map<number, () => void>} */
const tooltipHiders = new Map()

/**
 * Скрывает все тултипы, кроме указанного — одновременно виден только один.
 *
 * @param {number} id
 */
function claimTooltip(id) {
  for (const [otherId, hide] of tooltipHiders) {
    if (otherId !== id) hide()
  }
}
</script>
<script setup>
import { nextTick, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { createPopper } from '@popperjs/core'
import { getPageZoom, PAGE_ZOOM_EVENT } from '@/lib/pageZoom'

defineOptions({
  inheritAttrs: false,
})

const visible = ref(false)
const hideTimeout = ref(null)
const tooltipId = ++nextTooltipId
let alive = true

const r = useTemplateRef('r')
const rt = useTemplateRef('rt')
const p = ref(null)

const props = defineProps({
  text: String,
  show: { type: Boolean, default: true },
  hideDelay: { type: Number, default: 0 },
  /**
   * Сторона относительно контрола. bottom держит подсказку у кнопки:
   * auto уводил длинный текст влево, где больше места.
   */
  placement: { type: String, default: 'bottom' },
  /** inline-flex — для бейджей P/A и прочих inline-элементов */
  inline: { type: Boolean, default: false },
})

function clearHideTimeout() {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
}

function hideNow() {
  clearHideTimeout()
  visible.value = false
}

tooltipHiders.set(tooltipId, hideNow)

function onMouseEnter() {
  if (!props.show || !String(props.text || '').trim()) return
  claimTooltip(tooltipId)
  clearHideTimeout()
  visible.value = true
  // Popper после монтирования пузыря (v-if + Transition).
  nextTick(() => {
    syncPopper()
    requestAnimationFrame(() => {
      if (alive) p.value?.update()
    })
  })
}

function onMouseLeave() {
  const delay = props.hideDelay ?? 0
  if (delay <= 0) {
    visible.value = false
    return
  }
  clearHideTimeout()
  hideTimeout.value = setTimeout(() => {
    visible.value = false
    hideTimeout.value = null
  }, delay)
}

/** getBoundingClientRect — visual px, CSS zoom на html их ещё раз масштабирует. */
function pageZoomModifier() {
  return {
    name: 'pageZoom',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn({ state }) {
      const zoom = getPageZoom()
      if (!zoom || zoom === 1) return
      const offsets = state.modifiersData.popperOffsets
      if (offsets && state.styles.popper) {
        const x = Math.round(offsets.x / zoom)
        const y = Math.round(offsets.y / zoom)
        state.styles.popper.transform = `translate(${x}px, ${y}px)`
      }
      const arrow = state.modifiersData.arrow
      if (arrow && state.styles.arrow) {
        if (arrow.x != null) state.styles.arrow.left = `${arrow.x / zoom}px`
        if (arrow.y != null) state.styles.arrow.top = `${arrow.y / zoom}px`
      }
    },
  }
}

function popperOptions() {
  const placement = props.placement && props.placement !== 'auto' ? props.placement : 'bottom'
  return {
    placement,
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
          altAxis: true,
          tether: true,
          boundary: 'viewport',
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'right', 'left'],
          boundary: 'viewport',
        },
      },
      { name: 'arrow', options: { padding: 6 } },
      pageZoomModifier(),
    ],
  }
}

function destroyPopper() {
  if (p.value) {
    p.value.destroy()
    p.value = null
  }
}

function syncPopper() {
  if (!alive) return
  destroyPopper()
  if (!visible.value || !props.show || !rt.value || !r.value) return
  p.value = createPopper(r.value, rt.value, popperOptions())
  requestAnimationFrame(() => {
    if (alive) p.value?.update()
  })
}

function onPageZoom() {
  p.value?.update()
}

watch(
  [visible, () => props.show, () => props.text, () => props.placement],
  () => {
    nextTick(syncPopper)
  },
  { flush: 'post' }
)

window.addEventListener(PAGE_ZOOM_EVENT, onPageZoom)

onUnmounted(() => {
  alive = false
  tooltipHiders.delete(tooltipId)
  clearHideTimeout()
  destroyPopper()
  window.removeEventListener(PAGE_ZOOM_EVENT, onPageZoom)
})
</script>

<style scoped>
/* Teleport сохраняет scopeId — анимация fade снова работает с v-if */
.tooltip {
  transition: opacity 0.15s linear;
}

.tooltip.show {
  opacity: 1;
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.15s linear;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}
</style>
